import type { SxProps, Theme } from '@mui/material';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export type PageHeaderProps = {
  subtitle?: ReactNode;
  sx?: SxProps<Theme>;
} & (
  { title: ReactNode; loading?: false } | { title?: ReactNode; loading: true }
);

const contentSx = { maxWidth: '80%', margin: '0 auto' };

/**
 * Page title and subtitle, centered. Brings 8px above the title and its own
 * bottom padding: the caller supplies the rest of the top spacing and the
 * horizontal gutters (a `Container` or a width-capped box).
 */
export const PageHeader = ({
  title,
  subtitle,
  loading,
  sx,
}: PageHeaderProps) => {
  const sxList = Array.isArray(sx) ? sx : [sx];

  if (loading) {
    return (
      <Box sx={[{ pt: 1, pb: 4 }, ...sxList]}>
        <Stack sx={[contentSx, { alignItems: 'center' }]}>
          <Skeleton
            animation="wave"
            height="76px"
            width="240px"
            sx={{ mt: '-1.25rem' }}
          />
          <Skeleton
            animation="wave"
            height="28px"
            width="180px"
            sx={{ mt: '-0.75rem' }}
          />
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={[{ pt: 1, pb: { xs: 3, sm: 6, md: 8 } }, ...sxList]}>
      <Stack
        spacing={{ xs: 1, md: 0.75 }}
        sx={[contentSx, { alignItems: 'center' }]}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            textWrap: 'balance',
            textAlign: 'center',
            '.MuiDialog-root &': {
              fontSize: '36px',
            },
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="subtitle1"
            component="p"
            align="center"
            sx={{
              color: 'text.secondary',
              maxWidth: { md: '710px' },
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
